/**
 * Collection Carousel
 * - Arrow navigation (1 slide at a time)
 * - No loop (stops at edges)
 * - Touch swipe on mobile
 * - Responsive cards per view (desktop / mobile)
 */

class CollectionCarousel {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('[data-track]');
    this.prevBtn = container.querySelector('[data-arrow-prev]');
    this.nextBtn = container.querySelector('[data-arrow-next]');

    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.totalSlides = this.slides.length;

    if (this.totalSlides === 0) return;

    this.desktopCards = parseInt(container.dataset.cardsDesktop, 10) || 3;
    this.mobileCards = parseInt(container.dataset.cardsMobile, 10) || 1;
    this.gap = parseInt(container.dataset.cardGap, 10) || 16;

    this.currentIndex = 0;
    this.slideWidth = 0;
    this.cardsPerView = this.desktopCards;
    this.isDragging = false;
    this.wasDragged = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.dragThreshold = 50;

    this.init();
  }

  init() {
    this.updateCardsPerView();
    this.setSlideWidths();
    this.goToSlide(0, false);
    this.updateArrows();
    this.bindEvents();
  }

  setSlideWidths() {
    var viewport = this.container.querySelector('.collection-carousel__viewport');
    if (!viewport) return;

    var viewportWidth = viewport.clientWidth;
    var slideWidth;

    if (this.isMobile()) {
      if (this.mobileCards === 1) {
        slideWidth = viewportWidth * 0.85;
      } else {
        slideWidth = (viewportWidth - (this.mobileCards * this.gap)) / this.mobileCards;
      }
    } else {
      var totalGaps = this.cardsPerView - 1;
      slideWidth = (viewportWidth - (totalGaps * this.gap)) / this.cardsPerView;
    }

    this.slideWidth = slideWidth;
    this.slides.forEach(function(slide) {
      slide.style.width = slideWidth + 'px';
      slide.style.minWidth = slideWidth + 'px';
    });
  }

  get maxIndex() {
    return Math.max(0, this.totalSlides - this.cardsPerView);
  }

  getTranslateX(index) {
    return -(index * (this.slideWidth + this.gap));
  }

  goToSlide(index, animate) {
    if (animate === undefined) animate = true;
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    var translateX = this.getTranslateX(this.currentIndex);

    this.track.style.transition = animate
      ? 'transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)'
      : 'none';

    this.track.style.transform = 'translateX(' + translateX + 'px)';
    this.prevTranslate = translateX;
    this.currentTranslate = translateX;
    this.updateArrows();
  }

  next() {
    if (this.currentIndex < this.maxIndex) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  updateArrows() {
    if (this.prevBtn) {
      this.prevBtn.style.opacity = this.currentIndex <= 0 ? '0.3' : '1';
      this.prevBtn.style.pointerEvents = this.currentIndex <= 0 ? 'none' : 'auto';
    }
    if (this.nextBtn) {
      this.nextBtn.style.opacity = this.currentIndex >= this.maxIndex ? '0.3' : '1';
      this.nextBtn.style.pointerEvents = this.currentIndex >= this.maxIndex ? 'none' : 'auto';
    }
  }

  isMobile() {
    return window.innerWidth < 750;
  }

  updateCardsPerView() {
    this.cardsPerView = this.isMobile() ? this.mobileCards : this.desktopCards;
  }

  bindEvents() {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function() { self.prev(); });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function() { self.next(); });
    }

    this.track.addEventListener('touchstart', function(e) { self.onDragStart(e); }, { passive: true });
    this.track.addEventListener('touchmove', function(e) { self.onDragMove(e); }, { passive: false });
    this.track.addEventListener('touchend', function() { self.onDragEnd(); });

    this.track.addEventListener('mousedown', function(e) { self.onDragStart(e); });
    this.track.addEventListener('mousemove', function(e) { self.onDragMove(e); });
    this.track.addEventListener('mouseup', function() { self.onDragEnd(); });
    this.track.addEventListener('mouseleave', function() {
      if (self.isDragging) self.onDragEnd();
    });

    this.track.addEventListener('click', function(e) {
      if (self.wasDragged) {
        e.preventDefault();
        self.wasDragged = false;
      }
    });

    var resizeTimer;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function() { self.onResize(); }, 200);
    });
  }

  getPositionX(e) {
    return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
  }

  onDragStart(e) {
    this.isDragging = true;
    this.wasDragged = false;
    this.startX = this.getPositionX(e);
    this.track.classList.add('is-dragging');
    this.track.style.transition = 'none';
  }

  onDragMove(e) {
    if (!this.isDragging) return;

    var currentX = this.getPositionX(e);
    var diff = currentX - this.startX;

    if (Math.abs(diff) > 5) {
      this.wasDragged = true;
      if (e.cancelable && e.type === 'touchmove') {
        e.preventDefault();
      }
    }

    var newTranslate = this.prevTranslate + diff;
    var minTranslate = this.getTranslateX(this.maxIndex);

    if (newTranslate > 0) {
      newTranslate = newTranslate * 0.3;
    } else if (newTranslate < minTranslate) {
      newTranslate = minTranslate + (newTranslate - minTranslate) * 0.3;
    }

    this.currentTranslate = newTranslate;
    this.track.style.transform = 'translateX(' + this.currentTranslate + 'px)';
  }

  onDragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.track.classList.remove('is-dragging');

    var movedBy = this.currentTranslate - this.prevTranslate;

    if (Math.abs(movedBy) > this.dragThreshold) {
      if (movedBy < 0) {
        this.next();
      } else {
        this.prev();
      }
    } else {
      this.goToSlide(this.currentIndex);
    }
  }

  onResize() {
    this.updateCardsPerView();
    this.setSlideWidths();
    this.goToSlide(this.currentIndex, false);
  }
}

document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('.collection-carousel').forEach(function(el) {
    new CollectionCarousel(el);
  });
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(e) {
    var section = e.target.querySelector('.collection-carousel');
    if (section) new CollectionCarousel(section);
  });
}
